import { doc, getDoc, updateDoc, serverTimestamp, Timestamp } from 'firebase/firestore'
import { ZodError } from 'zod'
import { db } from '@/core/firebase/client'
import { ROOT_CLUBS, CLUB_NESTED_FIELDS } from '@/core/firebase/paths'
import { toFirestoreData } from '@/core/firebase/converters'
import { FirestoreRepository } from '@/core/firebase/repository'
import { ClubSchema, type Club } from '@/core/models/club'
import type { PricingRule } from '@/core/models/court'
import {
  SubscriptionPricing,
  SubscriptionSchema,
  type Subscription,
} from '@/core/models/subscription'
import { ClubFeaturesSchema, type ClubFeatures } from '@/core/models/club-features'
import { logger } from '@/core/logger'

/**
 * Clubs live at the root: `clubs/{clubId}`. Subscription and features are
 * nested maps on the same document — read via `getSubscription` / `getFeatures`,
 * write via `updateSubscription` / `updateFeatures`.
 */
export class ClubsRepository extends FirestoreRepository<typeof ClubSchema> {
  constructor() {
    super(ROOT_CLUBS, ClubSchema)
  }

  async getSubscription(clubId: string): Promise<Subscription | null> {
    const snap = await getDoc(doc(db, ROOT_CLUBS, clubId))
    if (!snap.exists()) return null
    const raw = snap.data()[CLUB_NESTED_FIELDS.subscription]
    if (!raw) return null
    try {
      return SubscriptionSchema.parse({ id: clubId, ...raw })
    } catch (e) {
      if (e instanceof ZodError) {
        logger.e(`subscription parse failed for ${clubId}`, e.issues)
      }
      return null
    }
  }

  async updateSubscription(clubId: string, partial: Partial<Subscription>): Promise<void> {
    const data = toFirestoreData(partial as Record<string, unknown>)
    const update: Record<string, unknown> = { updatedAt: serverTimestamp() }
    for (const [key, value] of Object.entries(data)) {
      update[`${CLUB_NESTED_FIELDS.subscription}.${key}`] = value
    }
    await updateDoc(doc(db, ROOT_CLUBS, clubId), update)
  }

  /**
   * Seed the nested `subscription` map with a fresh trial. Used when an
   * existing club doc was created before React-onboarding (or by a migration
   * path) and lacks a subscription. The billable court count lives on
   * `club.activeCourtCount` (maintained by the `on_court_written` trigger),
   * not inside the subscription map.
   */
  async startTrial(clubId: string): Promise<void> {
    const now = new Date()
    const trialEnd = new Date(
      now.getTime() + SubscriptionPricing.trialDaysRegular * 86_400_000,
    )
    await updateDoc(doc(db, ROOT_CLUBS, clubId), {
      // Lifecycle + billing live top-level now; the subscription map holds
      // Stripe metadata only (no `status`).
      status: 'trial',
      billingStatus: 'not_billable',
      trialEndDate: Timestamp.fromDate(trialEnd),
      [CLUB_NESTED_FIELDS.subscription]: {
        plan: 'monthly',
        partnerClub: false,
        trialStartDate: Timestamp.fromDate(now),
        trialEndDate: Timestamp.fromDate(trialEnd),
        paymentMethodAdded: false,
        cancelAtPeriodEnd: false,
      },
      updatedAt: serverTimestamp(),
    })
  }

  async getFeatures(clubId: string): Promise<ClubFeatures | null> {
    const snap = await getDoc(doc(db, ROOT_CLUBS, clubId))
    if (!snap.exists()) return null
    const raw = snap.data()[CLUB_NESTED_FIELDS.features] ?? {}
    try {
      return ClubFeaturesSchema.parse({ id: clubId, ...raw })
    } catch (e) {
      if (e instanceof ZodError) {
        logger.e(`features parse failed for ${clubId}`, e.issues)
      }
      return null
    }
  }

  async updateFeatures(clubId: string, partial: Partial<ClubFeatures>): Promise<void> {
    // Dotted-path write so a one-key patch doesn't wipe sibling features
    // (mirrors updateSubscription above). Nested maps inside features
    // (e.g. emailNotifications) are still replaced atomically by their
    // own key — callers writing a sub-map send the whole sub-map.
    const data = toFirestoreData(partial as Record<string, unknown>)
    const update: Record<string, unknown> = { updatedAt: serverTimestamp() }
    for (const [key, value] of Object.entries(data)) {
      update[`${CLUB_NESTED_FIELDS.features}.${key}`] = value
    }
    await updateDoc(doc(db, ROOT_CLUBS, clubId), update)
  }

  /** Owner confirmation and player-app activation are one atomic write. */
  async activatePlayerApp(clubId: string): Promise<void> {
    await updateDoc(doc(db, ROOT_CLUBS, clubId), {
      'features.onlineBookings': true,
      playerAppPricesAcceptedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })
  }

  /**
   * Replace the club-level default pricing config in one write. `rules` is
   * stored as a `Map<id, rule>` to mirror `court.pricingRules` (see
   * preprocess in `core/models/club.ts`). `slotDurationMinutes`, when
   * provided, sets `defaultSlotDurationMinutes` — the duration that
   * pre-fills when creating a new booking.
   */
  async updateDefaultPricing(
    clubId: string,
    payload: { rules: PricingRule[]; slotDurationMinutes?: number },
  ): Promise<void> {
    const rulesMap: Record<string, Omit<PricingRule, 'id'>> = {}
    for (const r of payload.rules) {
      rulesMap[r.id] = {
        daysOfWeek: r.daysOfWeek,
        startTime: r.startTime,
        endTime: r.endTime,
        price: r.price,
        pricingMode: r.pricingMode,
        priority: r.priority,
        ...(r.startDate != null ? { startDate: r.startDate } : {}),
        ...(r.endDate != null ? { endDate: r.endDate } : {}),
      }
    }
    const update: Record<string, unknown> = {
      defaultPricingRules: rulesMap,
      // Marker for the post-claim tutorial — owner has touched pricing
      // at least once. The tutorial only checks `!= null` so overwriting
      // on subsequent edits is fine.
      pricesReviewedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }
    if (payload.slotDurationMinutes !== undefined) {
      update['defaultSlotDurationMinutes'] = payload.slotDurationMinutes
    }
    await updateDoc(doc(db, ROOT_CLUBS, clubId), update)
  }

  /**
   * Owner-initiated soft delete. Flips the club to `closed` and stamps
   * `deletionRequestedAt` — the combination drives the ClubGate to route
   * to /pending-deletion (vs /suspended for an unpaid closure). The
   * actual data wipe happens later via the admin hard-delete Cloud
   * Function. Reversible by an admin until then.
   */
  async requestDeletion(clubId: string): Promise<void> {
    await updateDoc(doc(db, ROOT_CLUBS, clubId), {
      status: 'closed',
      deletionRequestedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })
  }

  /**
   * Best-effort write of `clubs/{clubId}.analyticsViewedAt` — used by the
   * owner tutorial to tick its "Revisa tu Rendimiento" step. Called from
   * the analytics page on first owner mount. We only test `!= null` so
   * overwriting on every visit is fine; failures are silent because this
   * is purely UI-state and shouldn't block the page render.
   *
   * Caller is responsible for gating to the owner role — managers may
   * also view analytics but the step is owner-shaped (the picker only
   * routes owners to OWNER_TUTORIAL).
   */
  async markAnalyticsViewed(clubId: string): Promise<void> {
    if (!clubId) return
    try {
      await updateDoc(doc(db, ROOT_CLUBS, clubId), {
        analyticsViewedAt: serverTimestamp(),
      })
    } catch (e) {
      logger.w('markAnalyticsViewed failed', e)
    }
  }

  /**
   * Best-effort stamp of `clubs/{clubId}.courtsReviewedAt` — used by the
   * owner tutorial's "Revisa tus pistas" step. Called from the courts
   * page on first owner mount. Tutorial only tests `!= null`, so
   * overwriting on subsequent visits is harmless.
   */
  async markCourtsReviewed(clubId: string): Promise<void> {
    if (!clubId) return
    try {
      await updateDoc(doc(db, ROOT_CLUBS, clubId), {
        courtsReviewedAt: serverTimestamp(),
      })
    } catch (e) {
      logger.w('markCourtsReviewed failed', e)
    }
  }

  /** Explicit owner confirmation: propagate errors so the UI never claims success. */
  async confirmSetupStep(clubId: string, step: 'courts' | 'hours' | 'prices'): Promise<void> {
    const fields = { courts: 'courtsReviewedAt', hours: 'hoursReviewedAt', prices: 'pricesReviewedAt' } as const
    await updateDoc(doc(db, ROOT_CLUBS, clubId), { [fields[step]]: serverTimestamp() })
  }

  /**
   * Best-effort stamp of `clubs/{clubId}.hoursReviewedAt` — used by the
   * owner tutorial's "Configura horario" step. Called from the settings
   * page on first owner mount of `?tab=hours`.
   */
  async markHoursReviewed(clubId: string): Promise<void> {
    if (!clubId) return
    try {
      await updateDoc(doc(db, ROOT_CLUBS, clubId), {
        hoursReviewedAt: serverTimestamp(),
      })
    } catch (e) {
      logger.w('markHoursReviewed failed', e)
    }
  }

  /**
   * Best-effort stamp of `clubs/{clubId}.pricesReviewedAt` — used by the
   * owner tutorial's "Revisa precios" step. Called from the settings page
   * on first owner view of `?tab=precios`, so the step completes on landing
   * (consistent with courts + hours) rather than only on a pricing save.
   * `updatePricing` also stamps it; tutorial only tests `!= null`.
   */
  async markPricesReviewed(clubId: string): Promise<void> {
    if (!clubId) return
    try {
      await updateDoc(doc(db, ROOT_CLUBS, clubId), {
        pricesReviewedAt: serverTimestamp(),
      })
    } catch (e) {
      logger.w('markPricesReviewed failed', e)
    }
  }

  /**
   * Best-effort stamp of `clubs/{clubId}.catalogReviewedAt` — used by the
   * owner tutorial's "Configura productos y servicios" step. Called from the
   * catalog page on first owner mount, so the step completes on landing
   * (consistent with courts + prices + hours).
   */
  async markCatalogReviewed(clubId: string): Promise<void> {
    if (!clubId) return
    try {
      await updateDoc(doc(db, ROOT_CLUBS, clubId), {
        catalogReviewedAt: serverTimestamp(),
      })
    } catch (e) {
      logger.w('markCatalogReviewed failed', e)
    }
  }
}

export const clubsRepository = new ClubsRepository()
export type { Club }
